/**
 * @author Kelnik
 *
 * Slider - Slider on slick-js
 */

import 'slick-carousel/slick/slick.scss';
import 'slick-carousel';
import {gsap, TweenMax} from 'gsap';
import Observer from 'common/scripts/observer';

const observer = new Observer();

gsap.config({
    nullTargetWarn: false
});

class Slider {
    constructor(options) {
        this.elem = options.elem;
        this.sliderElem = options.sliderElem;
        this.options = options;
        this.slickOptions = options.settings;
        this.isScrollTop = options.scrollTop;
        this.animationClass = 'is-animation';

        this.slideImgSelector = '.j-simple-slide-img';

        this.duration = options.duration || 0.6;
        this.ease = options.ease || 'ease-out';
        this.zoom = options.zoom || 1.3;
    }

    init() {
        this._initElements();
        this._setOptions();
        this._initSlick();
        this._subscribes();
    }

    /**
     * Ищем элемент по селектору или возвращаем сам элемент
     * @param {string|element} elem - строка-селектор или сам элемент
     * @returns {Element} - элемент
     * @private
     */
    static _getElement(elem) {
        return typeof elem === 'string' ? document.querySelector(elem) : elem;
    }

    /**
     * Определяем все нужные элементы
     * @private
     */
    _initElements() {
        this.slider = Slider._getElement(this.elem);
        this.controls = Slider._getElement(this.options.controls);
        this.counter = Slider._getElement(this.options.counter);

        if (this.controls) {
            this.appendDots = this.options.appendDots || this.controls.querySelector('.j-slider__dots');
            this.prevArrow = this.options.prevArrow || this.controls.querySelector('.j-slider__prev');
            this.nextArrow = this.options.nextArrow || this.controls.querySelector('.j-slider__next');
        }

        if (this.counter) {
            this.currentSlideWrapper = this.counter.querySelector('.j-slider__counter-current');
            this.countSlideWrapper = this.counter.querySelector('.j-slider__counter-all');
        }
    }

    /**
     * Смена счётчика слайдов
     * @param {object} event - событие slick-слайдера
     * @param {object} slickSlider - объект слайдера
     * @param {undefined| number} currentSlide - номер слайда слик слайдера (начиная с 0)
     * @private
     */
    _initCounter(event, slickSlider, currentSlide) {
        this.currentSlideWrapper.innerHTML = String((currentSlide ? currentSlide : 0) + 1);
        this.countSlideWrapper.innerHTML = slickSlider.slideCount;
    }

    /**
     * Добавляем в настройки слика элементы, куда вставить стрелки и точки
     * @private
     */
    _setOptions() {
        this.slickOptions.appendDots = this.appendDots;
        this.slickOptions.prevArrow = this.prevArrow;
        this.slickOptions.nextArrow = this.nextArrow;
    }

    /**
     * Инит слика
     * @private
     */
    _initSlick() {
        this.$slickElem = $(this.slider);

        if (this.counter) {
            this.$slickElem.on('init reInit afterChange', this._initCounter.bind(this));
        }

        this.$slickElem.on('init', this._initCallback.bind(this));
        this.$slickElem.on('breakpoint', this._slickAfterInit.bind(this));
        this.$slickElem.on('beforeChange', this._beforeChange.bind(this));
        this.$slickElem.on('afterChange', this._afterChange.bind(this));
        this.$slickElem.on('setPosition', this._setPosition.bind(this));

        this.$slickElem.slick(this.slickOptions);
        this._initAutoPlay();
        this._slickAfterInit();
    }

    _initAutoPlay() {
        if ('IntersectionObserver' in window && this.slickOptions.autoplay) {
            this._removeAutoplay();
            this.sliderObserver = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        this.$slickElem.slick('slickSetOption', 'autoplay', true, false);
                        this.$slickElem.slick('slickPlay');
                        if (typeof this.controls === 'object') {
                            this.controls.classList.add(this.animationClass);
                        }
                        this.sliderObserver.unobserve(this.slider);
                    }
                });
            });

            this.sliderObserver.observe(this.slider);
        }
    }

    _subscribes() {
        observer.subscribe('openFullText', () => {
            this.elem.classList.add('is-open');
        });

        observer.subscribe('parametricListOpened', () => {
            this.$slickElem.slick('reinit');
        });
    }

    _beforeChange(event, slick, currentSlide, nextSlide) {
        this.direction = this._getDirection(slick, currentSlide, nextSlide);
        this.currentSlideEl = slick.$slides.get(currentSlide);
        this.nextSlideEl = slick.$slides.get(nextSlide);

        const iframe = this.currentSlideEl.querySelector('iframe');

        if (iframe) {
            this._stopVideo(iframe);
        }

        observer.publish('closeAllFullText', this.currentSlideEl);

        this._setIndexSlide(this.currentSlideEl);
        this._animateSlideImage(this.currentSlideEl, this.nextSlideEl, this.direction);
    }

    _afterChange(event, slick, currentSlide) {
        const slide = slick.$slides.get(currentSlide);
        const imgEl = slide.querySelector(this.slideImgSelector);

        this._setDefaultIndexSlide(slick);
        this._clearJsStyles(imgEl);

        if (this.isScrollTop) {
            const top = this.sliderElem.getBoundingClientRect().top + pageYOffset;

            $('html, body').animate({scrollTop: top}, 400);
        }
    }

    _setPosition(event, slick) {
        this.current = slick.$slides.get(slick.currentSlide);

        setTimeout(() => {
            this._refreshSlideClip(this.current);
        }, 300);
    }

    _initCallback(event, slickSlider) {
        this.slider.classList.add('is-slider-ready');

        if (slickSlider.slideCount === 1) {
            this.slider.classList.add('is-block');
        }
    }

    _slickAfterInit() {
        if (typeof this.controls === 'object' && this.$slickElem.slick('slickGetOption', 'autoplay')) {
            this.controls.classList.add(this.animationClass);
        }

        this._setUserBehavior();
    }

    _setUserBehavior() {
        if (this.prevArrow && this.nextArrow) {
            this.prevArrow.addEventListener('click', this._removeAutoplay.bind(this));
            this.nextArrow.addEventListener('click', this._removeAutoplay.bind(this));
            this.$slickElem.on('swipe', this._removeAutoplay.bind(this));
            this.$slickElem.on('click', this._removeAutoplay.bind(this));
        }
    }

    _removeAutoplay() {
        if (this.$slickElem.slick('slickGetOption', 'autoplay')) {
            this.slickOptions.autoplay = false;
            this.$slickElem.slick('slickSetOption', 'autoplay', false, false);
            this.$slickElem.slick('slickPause');
            if (typeof this.controls === 'object') {
                this.controls.classList.remove(this.animationClass);
            }
        }
    }

    _clearJsStyles(element) {
        if (!element) {
            return;
        }

        element.removeAttribute('style');
    }

    _setDefaultIndexSlide(slick) {
        Array.from(slick.$slides).forEach((slide) => {
            slide.classList.remove('slick-prev');
        });
    }

    _animateSlideImage(currentSlideEl, nextSlideEl, direction) {
        const clip = this._calcClip(direction, nextSlideEl);

        this._toAnimate(clip, currentSlideEl, nextSlideEl);
    }

    _calcClip(direction, nextSlideEl) {
        const height = nextSlideEl.offsetHeight;
        const width = nextSlideEl.offsetWidth;

        if (direction === 'NEXT') {
            return {
                clipFrom: `rect(0, ${width}px, ${height}px, ${width}px)`,
                clipTo  : `rect(0, ${width}px, ${height}px, 0px)`
            };
        }

        return {
            clipFrom: `rect(0, 0, ${height}px, 0)`,
            clipTo  : `rect(0, ${width}px, ${height}px, 0px)`
        };
    }

    _toAnimate(clip, currentSlideEl, nextSlideEl) {
        const currentImageEl = currentSlideEl.querySelector(this.slideImgSelector);
        const nextImageEl = nextSlideEl.querySelector(this.slideImgSelector);

        TweenMax.set(nextImageEl, {
            scale: this.zoom,
            clip : clip.clipFrom
        });

        TweenMax.to(currentImageEl, this.duration, {
            scale: this.zoom,
            ease : this.ease
        });

        TweenMax.to(nextImageEl, this.duration, {
            scale: 1,
            clip : clip.clipTo,
            ease : this.ease
        });
    }

    _getDirection(slick, currentSlide, nextSlide) {
        const countSlide = slick.$slides.length;

        if (currentSlide === countSlide - 1 && nextSlide === 0) {
            return 'NEXT';
        }

        if (nextSlide === countSlide - 1 && currentSlide === 0) {
            return 'PREV';
        }

        if (currentSlide < nextSlide) {
            return 'NEXT';
        }

        return 'PREV';
    }

    _setIndexSlide(slide) {
        slide.classList.add('slick-prev');
    }

    _refreshSlideClip(current) {
        const imageEl = current.querySelector(this.slideImgSelector);

        TweenMax.set(imageEl, {clip: `rect(0, ${current.offsetWidth}px, ${current.offsetHeight}px, 0px)`});
    }

    on(event, func) {
        this.$slickElem.on(event, func);
    }

    _stopVideo(iframe) {
        if ((/youtube/).test(iframe.src) && (/enablejsapi=1/).test(iframe.src)) {
            iframe.contentWindow.postMessage('{"event":"command","func":"stopVideo","args":""}', '*');
        } else {
            const iframeSrc = iframe.src;

            iframe.src = iframeSrc;
        }
    }
}

export default Slider;
